// src/components/invoices/PendingInvoices.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./invoices.css";
import { fetchBillDateRanges, fetchPendingBills } from "../../services/invoices";
import InvoiceDetails from "./InvoiceDetails";

/* -------- helpers -------- */
function safeJson(v, fallback) {
  try {
    const s = typeof v === "string" ? v.trim() : v;
    return JSON.parse(s || "");
  } catch {
    return fallback;
  }
}

function pad2(n) {
  const s = String(n);
  return s.length === 1 ? `0${s}` : s;
}

function isoToLabel(iso) {
  try {
    const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

// server expects dd/MM/yyyy
function isoToDdMM(iso) {
  try {
    const [y, m, d] = String(iso || "").split("-");
    if (!y || !m || !d) return String(iso || "");
    return `${pad2(d)}/${pad2(m)}/${y}`;
  } catch {
    return String(iso || "");
  }
}

function moneyINR(x) {
  const n = Number(x || 0);
  const s = Math.abs(n).toFixed(2);
  return n < 0 ? `-₹${s}` : `₹${s}`;
}

/* localStorage keys */
function keyBillDatesJson(mainuser, pps) {
  return `billdates_json_${String(mainuser || "")}_${String(pps || "")}`;
}
function keyBillDatesETag(mainuser, pps) {
  return `billdates_etag_${String(mainuser || "")}_${String(pps || "")}`;
}
function keyPendingJson(mainuser, d1Iso, d2Iso) {
  return `pendingbills_json_${String(mainuser || "")}_${String(d1Iso || "")}_${String(d2Iso || "")}`;
}
function keyPendingETag(mainuser, d1Iso, d2Iso) {
  return `pendingbills_etag_${String(mainuser || "")}_${String(d1Iso || "")}_${String(d2Iso || "")}`;
}

export default function PendingInvoices({
  mainuser,
  selectedKendra,
  paymentperiodsettings: paymentperiodsettingsProp,

  // from Invoices.jsx
  initialDate1,
  initialDate2,

  onBack,

  // optional hooks (if parent passes)
  onEditCollections,
  onEditFeeds,
  onSaveInvoice,
}) {
  const paymentperiodsettings =
    paymentperiodsettingsProp ||
    selectedKendra?.vikriperiodsettings ||
    selectedKendra?.paymentperiodsettings ||
    "01-10,11-20,21-ME";

  // All ranges from server (ascending oldest->newest)
  const [allRanges, setAllRanges] = useState([]);
  const [visibleRanges, setVisibleRanges] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);

  // selected range ISO
  const [date1, setDate1] = useState(() => String(initialDate1 || "").trim());
  const [date2, setDate2] = useState(() => String(initialDate2 || "").trim());

  // pending rows
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(false);
  const [err, setErr] = useState("");

  // ui
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);

  // ---- race guards ----
  const activeRangeKeyRef = useRef(""); // `${d1Iso}|${d2Iso}`
  const reqSeqRef = useRef(0);
  const probeTokenRef = useRef(0);
  const didInitialProbeRef = useRef(false);

  // ✅ details state (NO dialog, same screen)
  const [showDetails, setShowDetails] = useState(false);
  const [selIdx, setSelIdx] = useState(-1); // index in filtered list

  const rangeLabel = useMemo(() => {
    if (!date1 || !date2) return "";
    return `${isoToLabel(date1)} - ${isoToLabel(date2)}`;
  }, [date1, date2]);

  /* ---------- Load date ranges ---------- */
  async function loadRanges() {
    if (!mainuser) return;

    setRangeLoading(true);
    setErr("");

    try {
      // cache-first
      const cacheJson = localStorage.getItem(keyBillDatesJson(mainuser, paymentperiodsettings)) || "";
      if (cacheJson) {
        const cachedArr = safeJson(cacheJson, []);
        const parsed = (Array.isArray(cachedArr) ? cachedArr : [])
          .map((o) => {
            const d1 = String(o?.date1 || "").trim();
            const d2 = String(o?.date2 || "").trim();
            if (!d1 || !d2) return null;
            return { date1: d1, date2: d2, label: `${isoToLabel(d1)} - ${isoToLabel(d2)}` };
          })
          .filter(Boolean);

        parsed.sort((a, b) =>
          a.date1 === b.date1 ? a.date2.localeCompare(b.date2) : a.date1.localeCompare(b.date1)
        );

        if (parsed.length) {
          setAllRanges(parsed);
          setVisibleRanges(parsed);
        }
      }

      const etag = localStorage.getItem(keyBillDatesETag(mainuser, paymentperiodsettings)) || null;
      const resp = await fetchBillDateRanges(mainuser, paymentperiodsettings, { etag });

      if (resp.status === 304) return;

      const arr = safeJson(resp.text, []);
      const parsed = (Array.isArray(arr) ? arr : [])
        .map((o) => {
          const d1 = String(o?.date1 || "").trim();
          const d2 = String(o?.date2 || "").trim();
          if (!d1 || !d2) return null;
          return { date1: d1, date2: d2, label: `${isoToLabel(d1)} - ${isoToLabel(d2)}` };
        })
        .filter(Boolean);

      parsed.sort((a, b) =>
        a.date1 === b.date1 ? a.date2.localeCompare(b.date2) : a.date1.localeCompare(b.date1)
      );

      setAllRanges(parsed);
      setVisibleRanges(parsed);

      localStorage.setItem(keyBillDatesJson(mainuser, paymentperiodsettings), JSON.stringify(arr));
      if (resp.etag) localStorage.setItem(keyBillDatesETag(mainuser, paymentperiodsettings), resp.etag);
    } catch (e) {
      setErr(e?.message || "Network Error");
      setAllRanges([]);
      setVisibleRanges([]);
    } finally {
      setRangeLoading(false);
    }
  }

  useEffect(() => {
    if (!mainuser) return;
    loadRanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainuser, paymentperiodsettings]);

  /* ---------- Load pending for selected range ---------- */
  async function loadPendingBills(d1Iso, d2Iso) {
    if (!mainuser || !d1Iso || !d2Iso) return;

    const mySeq = ++reqSeqRef.current;
    const myKey = `${d1Iso}|${d2Iso}`;
    activeRangeKeyRef.current = myKey;

    setLoading(true);
    setErr("");

    const d1Send = isoToDdMM(d1Iso);
    const d2Send = isoToDdMM(d2Iso);

    try {
      // cache-first
      const cachedBody = localStorage.getItem(keyPendingJson(mainuser, d1Iso, d2Iso)) || "";
      if (cachedBody) {
        const cached = safeJson(cachedBody, null);
        const data = cached?.data || cached?.rows || [];
        if (reqSeqRef.current === mySeq && activeRangeKeyRef.current === myKey) {
          setRows(Array.isArray(data) ? data : []);
          setTotals(cached?.totals || null);
        }
      }

      const etag = localStorage.getItem(keyPendingETag(mainuser, d1Iso, d2Iso)) || null;
      const resp = await fetchPendingBills(mainuser, d1Send, d2Send, { etag });

      if (reqSeqRef.current !== mySeq || activeRangeKeyRef.current !== myKey) return;
      if (resp.status === 304) return;

      const json = safeJson(resp.text, null);
      const data = json?.data || json?.rows || [];

      setRows(Array.isArray(data) ? data : []);
      setTotals(json?.totals || null);

      if (json) localStorage.setItem(keyPendingJson(mainuser, d1Iso, d2Iso), JSON.stringify(json));
      if (resp.etag) localStorage.setItem(keyPendingETag(mainuser, d1Iso, d2Iso), resp.etag);
    } catch (e) {
      if (reqSeqRef.current !== mySeq || activeRangeKeyRef.current !== myKey) return;
      setErr(e?.message || "Network Error");
      setRows([]);
      setTotals(null);
    } finally {
      if (reqSeqRef.current === mySeq && activeRangeKeyRef.current === myKey) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!date1 || !date2) return;
    if (probing) return;
    loadPendingBills(date1, date2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date1, date2, mainuser, probing]);

  /* ---------- Probe forward on open ---------- */
  useEffect(() => {
    if (!mainuser) return;
    if (!allRanges.length) return;
    if (didInitialProbeRef.current) return;

    didInitialProbeRef.current = true;
    const probeToken = ++probeTokenRef.current;

    const selD1 = String(initialDate1 || "").trim();
    const selD2 = String(initialDate2 || "").trim();

    async function probe() {
      setProbing(true);
      setErr("");

      let startIdx = 0;
      if (selD1 && selD2) {
        const idx = allRanges.findIndex((r) => r.date1 === selD1 && r.date2 === selD2);
        if (idx >= 0) startIdx = idx;
      }

      for (let i = startIdx; i < allRanges.length; i++) {
        if (probeTokenRef.current !== probeToken) return;

        const r = allRanges[i];
        const d1Send = isoToDdMM(r.date1);
        const d2Send = isoToDdMM(r.date2);

        try {
          const resp = await fetchPendingBills(mainuser, d1Send, d2Send, { etag: null });
          if (probeTokenRef.current !== probeToken) return;

          const json = safeJson(resp.text, null);
          const data = json?.data || json?.rows || [];
          const hasRows = Array.isArray(data) && data.length > 0;

          if (json) {
            try {
              localStorage.setItem(keyPendingJson(mainuser, r.date1, r.date2), JSON.stringify(json));
              if (resp.etag) localStorage.setItem(keyPendingETag(mainuser, r.date1, r.date2), resp.etag);
            } catch {}
          }

          if (hasRows) {
            activeRangeKeyRef.current = `${r.date1}|${r.date2}`;
            reqSeqRef.current++;

            setVisibleRanges(allRanges);
            setDate1(r.date1);
            setDate2(r.date2);

            setRows(data);
            setTotals(json?.totals || null);

            setProbing(false);
            return;
          }
        } catch {
          // ignore
        }
      }

      const fallback =
        selD1 && selD2 && allRanges.find((r) => r.date1 === selD1 && r.date2 === selD2)
          ? { date1: selD1, date2: selD2 }
          : allRanges[allRanges.length - 1];

      reqSeqRef.current++;
      setVisibleRanges(allRanges);
      setDate1(fallback.date1);
      setDate2(fallback.date2);
      setRows([]);
      setTotals(null);
      setProbing(false);
    }

    probe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainuser, allRanges]);

  // dropdown select
  function onSelectRange(v) {
    const found = visibleRanges.find((x) => `${x.date1}|${x.date2}` === v);
    if (!found) return;

    probeTokenRef.current++;
    reqSeqRef.current++;

    setDate1(found.date1);
    setDate2(found.date2);

    // also exit details
    setShowDetails(false);
    setSelIdx(-1);
  }

  // search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => {
      const kno = String(r?.kno || "").toLowerCase();
      const cname = String(r?.cname || "").toLowerCase();
      return kno.includes(q) || cname.includes(q);
    });
  }, [rows, search]);

  // pagination
  const totalEntries = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
  const safePage = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const showingFrom = totalEntries === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, totalEntries);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, date1, date2]);

  // ✅ open details in same screen
  function openDetailsByFilteredIndex(idx) {
    if (idx < 0 || idx >= filtered.length) return;
    setSelIdx(idx);
    setShowDetails(true);
  }

  function closeDetailsToList() {
    setShowDetails(false);
  }

  function goPrev() {
    if (selIdx <= 0) return;
    setSelIdx((x) => Math.max(0, x - 1));
  }

  function goNext() {
    if (selIdx < 0) return;
    if (selIdx >= filtered.length - 1) return;
    setSelIdx((x) => Math.min(filtered.length - 1, x + 1));
  }

  const selectedRow = selIdx >= 0 && selIdx < filtered.length ? filtered[selIdx] : null;
  const selectedKno = String(selectedRow?.kno || "");

  // ✅ RENDER DETAILS IN SAME PLACE (NO DIALOG)
  if (showDetails && selectedKno) {
    return (
      <InvoiceDetails
        mainuser={mainuser}
        kno={selectedKno}
        date1={date1}
        date2={date2}
        source="pending" // so Save Invoice button shows
        onBack={closeDetailsToList}
        indexLabel={`${selIdx + 1} / ${filtered.length}`}
        onPrev={selIdx > 0 ? goPrev : null}
        onNext={selIdx < filtered.length - 1 ? goNext : null}
        onEditCollections={onEditCollections}
        onEditFeeds={onEditFeeds}
        onSaveInvoice={onSaveInvoice}
      />
    );
  }

  // ✅ otherwise show list
  return (
    <div className="invPage">
      {/* Top bar */}
      <div className="invPendingTop">
        <button type="button" className="invBackBtn" onClick={() => (onBack ? onBack() : null)}>
          ←
        </button>
        <div className="invPendingTitle">Pending Invoice({totalEntries})</div>
      </div>

      {/* Date Range row */}
      <div className="invFilters">
        <div className="invFilterRow">
          <div className="invFilterLabel">Date Range :</div>

          <select
            className="invSelect"
            value={date1 && date2 ? `${date1}|${date2}` : ""}
            onChange={(e) => onSelectRange(e.target.value)}
            disabled={rangeLoading || visibleRanges.length === 0}
          >
            {visibleRanges.length === 0 ? (
              <option value="">{rangeLoading ? "Loading..." : "No ranges"}</option>
            ) : (
              visibleRanges.map((r) => (
                <option key={`${r.date1}|${r.date2}`} value={`${r.date1}|${r.date2}`}>
                  {r.label}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="invRangeLabel">{rangeLabel}</div>
      </div>

      {probing ? <div className="invMsg">Searching pending bills from selected range…</div> : null}
      {err ? <div className="invMsg err">{err}</div> : null}

      <div className="invCard">
        <div className="invCardHead">
          <div className="invCardTitle">
            List of Projected Invoices{" "}
            <span className="invSelected">(Projected Invoice Selected : 0)</span>
          </div>

          <div className="invHeadRight">
            <div className="invSearchBox">
              <input
                className="invSearchInput"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Projected Invoice"
              />
            </div>
          </div>
        </div>

        <div className="invTableWrap">
          <table className="invTable">
            <thead>
              <tr>
                <th className="chk" />
                <th colSpan={2}>Farmer Info</th>
                <th colSpan={1}>Milks</th>
                <th colSpan={3}>Deduction</th>
                <th colSpan={1}>Credit</th>
                <th colSpan={3}>Payment</th>
              </tr>
              <tr>
                <th className="chk" />
                <th>Code</th>
                <th>Name</th>
                <th>Quantity</th>
                <th>Management Charges</th>
                <th>In Hand Advance</th>
                <th>Total Deduction (A)</th>
                <th>Total Credit (B)</th>
                <th>Milk Amount (C)</th>
                <th>Total Amount (B + C)</th>
                <th>Balance</th>
              </tr>
            </thead>

            <tbody>
              {loading || probing ? (
                <tr>
                  <td colSpan={11} className="invMsg">Loading...</td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={11} className="invNoRecords">No Records Found.</td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const kno = String(r?.kno || "");
                  const cname = String(r?.cname || "");

                  const qty = Number(r?.qty ?? r?.liter ?? r?.total_liter ?? 0) || 0;
                  const mgmt = Number(r?.mgmt_cut ?? r?.management_charges ?? 0) || 0;
                  const inHand = Number(r?.in_hand_advance ?? 0) || 0;
                  const totalDedA = Number(r?.total_deduction ?? r?.kapat_cut ?? 0) || 0;
                  const totalCreditB = Number(r?.total_credit ?? 0) || 0;
                  const milkAmtC = Number(r?.milk_amt ?? r?.gross_amt ?? r?.amt ?? 0) || 0;
                  const totalBC = totalCreditB + milkAmtC;
                  const bal = Number(r?.overall_bal ?? r?.balance ?? 0) || 0;

                  const filteredIndex = (safePage - 1) * pageSize + idx;

                  return (
                    <tr
                      key={`${kno}-${idx}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => openDetailsByFilteredIndex(filteredIndex)}
                      title="Open Invoice Details"
                    >
                      <td className="chk" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" readOnly />
                      </td>
                      <td>{kno}</td>
                      <td><b>{cname}</b></td>
                      <td className="num">{qty.toFixed(2)}</td>
                      <td className="num">{moneyINR(mgmt)}</td>
                      <td className="num">{moneyINR(inHand)}</td>
                      <td className="num">{moneyINR(totalDedA)}</td>
                      <td className="num">{moneyINR(totalCreditB)}</td>
                      <td className="num">{moneyINR(milkAmtC)}</td>
                      <td className="num">{moneyINR(totalBC)}</td>
                      <td className={`num ${bal < 0 ? "neg" : ""}`}>{moneyINR(bal)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="invFooter">
          <div className="invShowing">
            Showing {showingFrom} to {showingTo} of {totalEntries} entries
          </div>

          <div className="invPager">
            <button type="button" className="invPgBtn" onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
            <button type="button" className="invPgBtn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
            <button type="button" className="invPgNum active">{safePage}</button>
            <button type="button" className="invPgBtn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
            <button type="button" className="invPgBtn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</button>

            <select className="invPageSize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {totals ? (
          <div className="invTotalsBar">
            <div>Avg Rate: <b>{Number(totals?.avg_rate || 0).toFixed(2)}</b></div>
            <div>Total Liter: <b>{Number(totals?.total_liter || 0).toFixed(2)}</b></div>
            <div>Total Amount: <b>{moneyINR(totals?.total_amt || 0)}</b></div>
            <div>Total Kapat: <b>{moneyINR(totals?.total_kapat || 0)}</b></div>
            <div>Total Payment: <b>{moneyINR(totals?.total_payment || 0)}</b></div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
